/**
 * LeadershipViewPage - Public department leadership sentiment report view.
 * Department heads access their Tier 1 report via a token link.
 * No admin auth required - token-based access only.
 *
 * Accessed via /leadership/{token}
 */
import React, { useState, useEffect } from 'react';
import {
    Loader2, AlertTriangle, Download, Users, Calendar,
    Cog, Monitor, MessageCircle,
    Star, AlertCircle, Lightbulb,
    ChevronRight, Shield, CheckCircle2, Gauge
} from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import '../styles/leadership-view.css';

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function normalizeTerminology(value) {
    return String(value)
        .replace(/\bMicrosoft Teams\b/gi, 'Teams')
        .replace(/\bSlack\b/gi, 'Teams')
        .replace(/\bPeople Business Partners?\b/gi, 'People Business Partners')
        .replace(/\bPBP'?s\b/gi, 'People Business Partners')
        .replace(/\bPBP\b/gi, 'People Business Partners');
}

/**
 * Coerce any value (string, object, array) into a short, human-readable
 * string. Defensive: older saved reports stored tools as objects like
 * {area, tool, pain_point, ...} - without this the JSX would crash with
 * "Objects are not valid as a React child" and blank-screen the page.
 */
function toLabel(value) {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number') return normalizeTerminology(value);
    if (Array.isArray(value)) return value.map(toLabel).filter(Boolean).join(', ');
    if (typeof value === 'object') {
        // Prefer a short headline field if present, otherwise stitch the
        // first couple of meaningful fields together.
        const preferred = value.label || value.title || value.name || value.tool
            || value.area || value.pain_point || value.summary;
        if (preferred) return normalizeTerminology(preferred);
        const parts = Object.values(value)
            .filter(v => typeof v === 'string' && v.trim())
            .slice(0, 2);
        return normalizeTerminology(parts.join(' - '));
    }
    return normalizeTerminology(value);
}

function Badge({ children, variant = 'default' }) {
    const styles = {
        default:  { bg: '#e8f0f0', color: '#1a3a3a' },
        positive: { bg: '#e8f5e9', color: '#2e7d32' },
        warning:  { bg: '#fff3e0', color: '#e65100' },
        danger:   { bg: '#fde8e8', color: '#c62828' },
        info:     { bg: '#e3f2fd', color: '#1565c0' },
    };
    const s = styles[variant] || styles.default;
    return (
        <span className="lv-badge" style={{ background: s.bg, color: s.color }}>
            {children}
        </span>
    );
}

function SectionCard({ icon: Icon, iconColor, title, tag, children }) {
    return (
        <section className="lv-card">
            <h2 className="lv-card-title">
                {Icon && <Icon size={18} style={{ color: iconColor || '#1a3a3a' }} />}
                <span>{title}</span>
                {tag && <span className="lv-section-tag">{tag}</span>}
            </h2>
            <div className="lv-card-body">
                {children}
            </div>
        </section>
    );
}

function asList(value) {
    return Array.isArray(value) ? value : [];
}

function frictionVariant(value) {
    const friction = String(value || '').toLowerCase();
    if (friction === 'broken' || friction === 'significant_friction') return 'danger';
    if (friction === 'minor_friction') return 'warning';
    if (friction === 'smooth') return 'positive';
    return 'default';
}

function healthVariant(value) {
    const health = String(value || '').toLowerCase();
    if (['strong', 'high', 'smooth'].includes(health)) return 'positive';
    if (['adequate', 'moderate', 'mixed', 'minor_friction'].includes(health)) return 'warning';
    if (['strained', 'low', 'needs_improvement', 'needs_attention', 'broken', 'significant_friction'].includes(health)) return 'danger';
    return 'default';
}

function confidenceVariant(value) {
    const confidence = String(value || '').toLowerCase();
    if (confidence === 'high') return 'positive';
    if (confidence === 'medium') return 'warning';
    if (confidence === 'low') return 'danger';
    return 'default';
}

function confidenceScore(value) {
    const confidence = String(value || '').toLowerCase();
    if (confidence === 'high') return 88;
    if (confidence === 'medium') return 58;
    if (confidence === 'low') return 32;
    return 45;
}

function variantWeight(value) {
    const variant = value || 'default';
    if (variant === 'danger') return 1;
    if (variant === 'warning') return 2;
    if (variant === 'info') return 3;
    if (variant === 'positive') return 4;
    return 5;
}

function frequencyWeight(value) {
    const text = toLabel(value).toLowerCase();
    if (text.includes('daily')) return 1;
    if (text.includes('bi-week') || text.includes('biweekly') || text.includes('payroll')) return 2;
    if (text.includes('week')) return 3;
    if (text.includes('month')) return 4;
    if (text.includes('cycle') || text.includes('season')) return 5;
    if (text.includes('event')) return 6;
    return 9;
}

function frictionSeverity(item) {
    const text = [item?.impact, item?.prevalence, item?.root_cause_indicators]
        .map(toLabel)
        .join(' ')
        .toLowerCase();
    if (/critical|severe|majority|most|significant|high|widespread|multiple|30|40|50|daily/.test(text)) {
        return 'danger';
    }
    if (/moderate|recurring|several|some|weekly|concentrated/.test(text)) {
        return 'warning';
    }
    return 'info';
}

function estimateWeeklyHours(value) {
    const text = toLabel(value).replace(/[\u2013\u2014]/g, '-');
    const matches = text.match(/\d+(?:\.\d+)?/g);
    if (!matches || matches.length === 0) return null;
    const numbers = matches.map(Number).filter(Number.isFinite);
    if (numbers.length === 0) return null;
    if (numbers.length === 1) return numbers[0];
    return (numbers[0] + numbers[1]) / 2;
}

function frequencyBucket(value) {
    const text = toLabel(value).toLowerCase();
    if (text.includes('daily')) return { key: 'daily', title: 'Daily', variant: 'danger', order: 1 };
    if (text.includes('bi-week') || text.includes('biweekly') || text.includes('payroll')) {
        return { key: 'biweekly', title: 'Bi-weekly / Payroll Cycle', variant: 'danger', order: 2 };
    }
    if (text.includes('week')) return { key: 'weekly', title: 'Weekly', variant: 'warning', order: 3 };
    if (text.includes('month')) return { key: 'monthly', title: 'Monthly', variant: 'warning', order: 4 };
    if (text.includes('cycle') || text.includes('season')) return { key: 'cyclical', title: 'Cyclical', variant: 'info', order: 5 };
    if (text.includes('event')) return { key: 'event-driven', title: 'Event-Driven', variant: 'info', order: 6 };
    return { key: 'other', title: 'Other / Unspecified', variant: 'default', order: 99 };
}

function formatHours(value) {
    if (value == null || Number.isNaN(value)) return null;
    const rounded = Math.round(value * 10) / 10;
    return `${rounded.toLocaleString()} hrs/wk`;
}

function prettyToken(value) {
    return toLabel(value).replace(/_/g, ' ');
}

function LeadershipViewPage({ token }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadReport = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE_URL}/api/public/leadership/${token}`);
                if (res.status === 403) {
                    const body = await res.json();
                    setError(body.detail || 'This link has expired or been revoked.');
                    return;
                }
                if (res.status === 404) {
                    setError('Report not found. This link may be invalid.');
                    return;
                }
                if (!res.ok) throw new Error('Failed to load report');
                setData(await res.json());
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        loadReport();
    }, [token]);

    const handleDownloadPdf = () => {
        window.open(`${API_BASE_URL}/api/public/leadership/${token}/pdf`, '_blank');
    };

    /* ---------- Loading ---------- */
    if (loading) {
        return (
            <div className="lv-page">
                <div className="lv-status-card">
                    <Loader2 size={28} className="lv-spinner" />
                    <p>Loading department report...</p>
                </div>
            </div>
        );
    }

    /* ---------- Error ---------- */
    if (error) {
        return (
            <div className="lv-page">
                <div className="lv-status-card">
                    <AlertTriangle size={28} style={{ color: '#c62828' }} />
                    <h2>Unable to Load Report</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const report = data.report || {};
    const process = report.process_themes || {};
    const handoff = report.process_handoff_map || {};
    const tech = report.technology_health || {};
    const collab = report.collaboration_and_communication || {};

    const frictionAreas = asList(process.top_friction_areas)
        .slice()
        .sort((a, b) => variantWeight(frictionSeverity(a)) - variantWeight(frictionSeverity(b)));
    const manualTasks = asList(process.repetitive_manual_tasks);
    const automationCandidates = asList(process.ai_automation_candidates);
    const outsourcingCandidates = asList(process.outsourcing_offshoring_candidates);
    const roleScopeObservations = asList(process.role_scoping_observations);
    const highConfidenceAutomations = automationCandidates.filter(
        candidate => confidenceVariant(candidate?.confidence) === 'positive'
    ).length;
    const manualTaskGroups = Object.values(manualTasks.reduce((groups, task) => {
        const bucket = frequencyBucket(task?.frequency);
        const estimatedHours = estimateWeeklyHours(task?.estimated_time_per_week);
        if (!groups[bucket.key]) {
            groups[bucket.key] = {
                ...bucket,
                tasks: [],
                estimatedHours: 0,
                hasEstimate: false,
            };
        }
        groups[bucket.key].tasks.push({ ...task, estimatedHours });
        if (estimatedHours != null) {
            groups[bucket.key].estimatedHours += estimatedHours;
            groups[bucket.key].hasEstimate = true;
        }
        return groups;
    }, {})).sort((a, b) => a.order - b.order);
    const totalManualHours = manualTaskGroups.reduce(
        (total, group) => total + (group.hasEstimate ? group.estimatedHours : 0),
        0,
    );

    const handoffs = asList(handoff.handoffs)
        .slice()
        .sort((a, b) => (
            variantWeight(frictionVariant(a?.friction_level)) - variantWeight(frictionVariant(b?.friction_level))
        ) || (frequencyWeight(a?.frequency) - frequencyWeight(b?.frequency)));
    const crossDeptTouchpoints = asList(handoff.cross_department_touchpoints)
        .slice()
        .sort((a, b) => variantWeight(healthVariant(a?.health)) - variantWeight(healthVariant(b?.health)));
    const highFrictionHandoffs = handoffs.filter(h => ['danger'].includes(frictionVariant(h.friction_level))).length;
    const crossDeptHandoffs = handoffs.filter(h => h.cross_department).length;
    const smoothHandoffs = handoffs.filter(h => frictionVariant(h.friction_level) === 'positive').length;

    const techGroups = [
        {
            title: 'Working Well',
            items: asList(tech.tools_working_well),
            variant: 'positive',
            icon: CheckCircle2,
        },
        {
            title: 'Causing Friction',
            items: asList(tech.tools_causing_friction),
            variant: 'danger',
            icon: AlertTriangle,
        },
        {
            title: 'Tool Gaps',
            items: asList(tech.tool_gaps),
            variant: 'warning',
            icon: Lightbulb,
        },
    ];
    const techSignalCount = techGroups.reduce((total, group) => total + group.items.length, 0);

    const collabDimensions = [
        { label: 'Team Dynamics', value: collab.team_dynamics, variant: 'positive' },
        { label: 'Cross-Team Collaboration', value: collab.cross_team_collaboration, variant: 'warning' },
        { label: 'Communication Effectiveness', value: collab.communication_effectiveness, variant: 'info' },
        { label: 'Meeting Culture', value: collab.meeting_culture, variant: 'default' },
    ].filter(item => item.value);

    return (
        <div className="lv-page">
            <div className="lv-container">

                {/* ---- Header ---- */}
                <header className="lv-header">
                    <div className="lv-header-left">
                        <h1 className="lv-header-title">{data.department} - Leadership Report</h1>
                        <p className="lv-header-meta">
                            <Users size={14} />
                            <span>{data.respondent_count} respondent{data.respondent_count !== 1 ? 's' : ''}</span>
                            <span className="lv-meta-sep">&bull;</span>
                            <Calendar size={14} />
                            <span>{formatDate(data.generated_at)}</span>
                        </p>
                        {data.label && <p className="lv-header-label">{data.label}</p>}
                    </div>
                    <button className="lv-download-btn" onClick={handleDownloadPdf}>
                        <Download size={16} />
                        Download PDF
                    </button>
                </header>

                <div className="lv-validation-banner">
                    <Shield size={16} />
                    <div>
                        <strong>Validation only</strong>
                        <span>
                            Use this report to confirm themes, correct context, and guide discussion. It is not an action plan or approval to implement changes.
                        </span>
                    </div>
                </div>

                <div className="lv-report-legend" aria-label="Report legend">
                    <div className="lv-legend-group">
                        <span className="lv-legend-label">Drag</span>
                        <Badge variant="danger">high drag</Badge>
                        <Badge variant="warning">moderate drag</Badge>
                        <Badge variant="info">watch</Badge>
                    </div>
                    <div className="lv-legend-group">
                        <span className="lv-legend-label">Automation</span>
                        <Badge variant="positive">ready to explore</Badge>
                        <Badge variant="warning">needs scoping</Badge>
                    </div>
                    <div className="lv-legend-group">
                        <span className="lv-legend-label">Cadence</span>
                        <Badge variant="danger">daily / payroll-cycle</Badge>
                        <Badge variant="warning">weekly / monthly</Badge>
                    </div>
                </div>

                {/* ---- Executive Summary ---- */}
                {report.executive_summary && (
                    <SectionCard icon={Star} iconColor="#d4a574" title="Executive Summary">
                        <p className="lv-summary-text">{toLabel(report.executive_summary)}</p>
                    </SectionCard>
                )}

                {process.process_strengths?.length > 0 && (
                    <SectionCard icon={CheckCircle2} iconColor="#2e7d32" title="What's Working" tag="Start Here">
                        <div className="lv-whats-working-grid">
                            {process.process_strengths.map((strength, i) => (
                                <div key={i} className="lv-whats-working-card">
                                    <CheckCircle2 size={16} />
                                    <span>{toLabel(strength)}</span>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                )}

                {/* ---- Process Themes (PRIMARY FOCUS) ---- */}
                <SectionCard icon={Cog} iconColor="#7fb3b3" title="Process Themes" tag="Validation Only">
                    {process.efficiency_rating && (
                        <div className="lv-metric-row">
                            <div className="lv-metric">
                                <span className="lv-metric-label">Efficiency Rating</span>
                                <span className="lv-metric-value">{process.efficiency_rating}</span>
                            </div>
                        </div>
                    )}

                    {process.efficiency_analysis && (
                        <div className="lv-subsection">
                            <p className="lv-summary-text">{toLabel(process.efficiency_analysis)}</p>
                        </div>
                    )}

                    {(frictionAreas.length > 0 || automationCandidates.length > 0 || manualTasks.length > 0 || roleScopeObservations.length > 0) && (
                        <div className="lv-process-signal-grid">
                            <div className="lv-process-signal lv-process-signal-danger">
                                <span>{frictionAreas.length}</span>
                                <strong>friction areas</strong>
                            </div>
                            <div className="lv-process-signal lv-process-signal-warning">
                                <span>{manualTasks.length}</span>
                                <strong>manual tasks</strong>
                            </div>
                            <div className="lv-process-signal lv-process-signal-positive">
                                <span>{automationCandidates.length}</span>
                                <strong>automation candidates</strong>
                            </div>
                            <div className="lv-process-signal lv-process-signal-info">
                                <span>{roleScopeObservations.length}</span>
                                <strong>role-scope signals</strong>
                            </div>
                        </div>
                    )}

                    {frictionAreas.length > 0 && (
                        <div className="lv-subsection">
                            <div className="lv-subsection-header-row">
                                <h3 className="lv-subsection-title">Top Friction Areas</h3>
                                <span className="lv-section-count">ranked by operating drag</span>
                            </div>
                            <div className="lv-friction-rank-grid">
                                {frictionAreas.map((f, i) => {
                                    const severity = frictionSeverity(f);
                                    const evidence = asList(f.evidence_snippets);
                                    return (
                                    <div key={i} className={`lv-friction-visual-card lv-friction-${severity}`}>
                                        <div className="lv-friction-rank">#{i + 1}</div>
                                        <div className="lv-friction-visual-body">
                                            <div className="lv-friction-visual-header">
                                                <h4>
                                                    <AlertCircle size={15} />
                                                    {toLabel(f.area || f.title) || 'Friction Area'}
                                                </h4>
                                                <Badge variant={severity}>{severity === 'danger' ? 'high drag' : severity === 'warning' ? 'moderate drag' : 'watch'}</Badge>
                                            </div>
                                            {f.detailed_description && (
                                                <p className="lv-friction-desc">{toLabel(f.detailed_description)}</p>
                                            )}
                                            <div className="lv-friction-field-grid">
                                                {f.prevalence && (
                                                    <div className="lv-friction-field">
                                                        <span>Prevalence</span>
                                                        <strong>{toLabel(f.prevalence)}</strong>
                                                    </div>
                                                )}
                                                {f.impact && (
                                                    <div className="lv-friction-field lv-friction-field-impact">
                                                        <span>Impact</span>
                                                        <strong>{toLabel(f.impact)}</strong>
                                                    </div>
                                                )}
                                                {f.root_cause_indicators && (
                                                    <div className="lv-friction-field lv-friction-field-wide">
                                                        <span>Likely Driver</span>
                                                        <strong>{toLabel(f.root_cause_indicators)}</strong>
                                                    </div>
                                                )}
                                            </div>
                                            {evidence.length > 0 && (
                                                <div className="lv-evidence-chip-row">
                                                    <span className="lv-evidence-count">{evidence.length} evidence signal{evidence.length !== 1 ? 's' : ''}</span>
                                                    {evidence.slice(0, 2).map((e, j) => (
                                                        <span key={j} className="lv-evidence-chip">&ldquo;{toLabel(e)}&rdquo;</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {manualTaskGroups.length > 0 && (
                        <div className="lv-subsection">
                            <div className="lv-subsection-header-row">
                                <h3 className="lv-subsection-title">Repetitive / Manual Tasks Surfaced</h3>
                                <span className="lv-section-count">
                                    {formatHours(totalManualHours) || 'estimate unavailable'} potential weekly drag
                                </span>
                            </div>
                            <div className="lv-manual-frequency-lanes">
                                {manualTaskGroups.map(group => (
                                    <div key={group.key} className={`lv-manual-lane lv-manual-lane-${group.variant}`}>
                                        <div className="lv-manual-lane-header">
                                            <div>
                                                <span>{group.title}</span>
                                                <strong>{group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}</strong>
                                            </div>
                                            {group.hasEstimate && (
                                                <em>{formatHours(group.estimatedHours)}</em>
                                            )}
                                        </div>
                                        <div className="lv-manual-task-stack">
                                            {group.tasks
                                                .slice()
                                                .sort((a, b) => (b.estimatedHours || 0) - (a.estimatedHours || 0))
                                                .map((task, i) => (
                                                    <div key={i} className="lv-manual-task-card">
                                                        <div className="lv-manual-task-header">
                                                            <strong>{toLabel(task.task)}</strong>
                                                            {task.estimated_time_per_week && (
                                                                <Badge variant={group.variant}>{toLabel(task.estimated_time_per_week)}</Badge>
                                                            )}
                                                        </div>
                                                        <div className="lv-manual-task-meta">
                                                            {task.currently_done_by && (
                                                                <span><b>Owner:</b> {toLabel(task.currently_done_by)}</span>
                                                            )}
                                                            {task.frequency && (
                                                                <span><b>Cadence:</b> {toLabel(task.frequency)}</span>
                                                            )}
                                                        </div>
                                                        {task.why_manual && (
                                                            <div className="lv-manual-task-why">
                                                                <AlertCircle size={13} />
                                                                <span>{toLabel(task.why_manual)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {automationCandidates.length > 0 && (
                        <div className="lv-subsection">
                            <div className="lv-subsection-header-row">
                                <h3 className="lv-subsection-title">AI / Automation Candidates Surfaced</h3>
                                <span className="lv-section-count">{highConfidenceAutomations} high-confidence</span>
                            </div>
                            <div className="lv-automation-lanes">
                                {[
                                    { key: 'high', title: 'Ready to Explore', variant: 'positive' },
                                    { key: 'medium', title: 'Needs Scoping', variant: 'warning' },
                                    { key: 'low', title: 'Watch / Validate', variant: 'danger' },
                                    { key: 'other', title: 'Unrated', variant: 'info' },
                                ].map(lane => {
                                    const laneItems = automationCandidates.filter(candidate => {
                                        const confidence = String(candidate?.confidence || '').toLowerCase();
                                        return lane.key === 'other'
                                            ? !['high', 'medium', 'low'].includes(confidence)
                                            : confidence === lane.key;
                                    });
                                    if (laneItems.length === 0) return null;
                                    return (
                                        <div key={lane.key} className={`lv-automation-lane lv-automation-lane-${lane.variant}`}>
                                            <div className="lv-automation-lane-header">
                                                <span>{lane.title}</span>
                                                <strong>{laneItems.length}</strong>
                                            </div>
                                            <div className="lv-automation-card-stack">
                                                {laneItems.map((c, i) => {
                                                    const variant = confidenceVariant(c.confidence);
                                                    return (
                                                        <div key={i} className={`lv-automation-card lv-automation-${variant}`}>
                                                            <div className="lv-automation-card-header">
                                                                <strong>{toLabel(c.task)}</strong>
                                                                {c.confidence && <Badge variant={variant}>{prettyToken(c.confidence)}</Badge>}
                                                            </div>
                                                            <div className="lv-confidence-meter" aria-hidden="true">
                                                                <span style={{ width: `${confidenceScore(c.confidence)}%` }} />
                                                            </div>
                                                            {c.approach && (
                                                                <div className="lv-automation-approach">
                                                                    <Lightbulb size={13} />
                                                                    {toLabel(c.approach)}
                                                                </div>
                                                            )}
                                                            {c.rationale && <p>{toLabel(c.rationale)}</p>}
                                                            {c.blockers && (
                                                                <div className="lv-automation-blocker">
                                                                    <AlertCircle size={13} />
                                                                    <span>{toLabel(c.blockers)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {outsourcingCandidates.length > 0 && (
                        <div className="lv-subsection">
                            <div className="lv-subsection-header-row">
                                <h3 className="lv-subsection-title">Outsourcing / Offshoring Candidates Surfaced</h3>
                                <span className="lv-section-count">process candidates only</span>
                            </div>
                            <div className="lv-card-list">
                                {outsourcingCandidates.map((c, i) => (
                                    <div key={i} className="lv-mini-card lv-sourcing-card">
                                        <div className="lv-mini-card-header">
                                            <strong>{toLabel(c.task)}</strong>
                                            {c.internal_knowledge_required && (
                                                <Badge variant={
                                                    c.internal_knowledge_required === 'high' ? 'danger'
                                                        : c.internal_knowledge_required === 'low' ? 'positive'
                                                            : 'warning'
                                                }>
                                                    {toLabel(c.internal_knowledge_required)} internal knowledge
                                                </Badge>
                                            )}
                                        </div>
                                        {c.rationale && <p>{toLabel(c.rationale)}</p>}
                                        {c.risk_if_outsourced && (
                                            <p className="lv-mini-card-meta">
                                                <strong>Risk if outsourced:</strong> {toLabel(c.risk_if_outsourced)}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {process.role_scoping_observations?.length > 0 && (
                        <div className="lv-subsection">
                            <h3 className="lv-subsection-title">Role-Scoping Observations</h3>
                            <ul className="lv-bullet-list">
                                {process.role_scoping_observations.map((o, i) => (
                                    <li key={i}>
                                        <strong>{toLabel(o.observation)}</strong>
                                        {o.evidence && <> &mdash; {toLabel(o.evidence)}</>}
                                        {o.implication && (
                                            <div className="lv-mini-card-meta">
                                                <em>Implication:</em> {toLabel(o.implication)}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {process.streamlining_opportunities?.length > 0 && (
                        <div className="lv-subsection">
                            <div className="lv-subsection-header-row">
                                <h3 className="lv-subsection-title">Streamlining Opportunities Surfaced</h3>
                                <span className="lv-section-count lv-section-count-guard">validation only</span>
                            </div>
                            <div className="lv-streamlining-guard">
                                These are candidate themes for validation and discussion. Do not treat this section as recommendations, approvals, or an implementation plan.
                            </div>
                            <ul className="lv-bullet-list lv-opportunity">
                                {process.streamlining_opportunities.map((o, i) => (
                                    <li key={i}>
                                        <Lightbulb size={14} />
                                        <span>
                                            <strong>{toLabel(o.opportunity)}</strong>
                                            {o.potential_impact && <> &mdash; {toLabel(o.potential_impact)}</>}
                                            {o.implementation_complexity && (
                                                <Badge variant="info">
                                                    {toLabel(o.implementation_complexity)} complexity
                                                </Badge>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </SectionCard>

                {/* ---- Process Handoff Map ---- */}
                {(handoffs.length > 0 || crossDeptTouchpoints.length > 0) && (
                    <SectionCard icon={ChevronRight} iconColor="#1a3a3a" title="Process Handoff Map" tag="Validation Only">
                        {handoff.summary && (
                            <p className="lv-summary-text">{toLabel(handoff.summary)}</p>
                        )}

                        {handoffs.length > 0 && (
                            <div className="lv-visual-stat-grid">
                                <div className="lv-visual-stat">
                                    <span className="lv-visual-stat-value">{handoffs.length}</span>
                                    <span className="lv-visual-stat-label">handoffs mapped</span>
                                </div>
                                <div className="lv-visual-stat lv-visual-stat-danger">
                                    <span className="lv-visual-stat-value">{highFrictionHandoffs}</span>
                                    <span className="lv-visual-stat-label">high-friction</span>
                                </div>
                                <div className="lv-visual-stat lv-visual-stat-info">
                                    <span className="lv-visual-stat-value">{crossDeptHandoffs}</span>
                                    <span className="lv-visual-stat-label">cross-dept</span>
                                </div>
                                <div className="lv-visual-stat lv-visual-stat-positive">
                                    <span className="lv-visual-stat-value">{smoothHandoffs}</span>
                                    <span className="lv-visual-stat-label">smooth</span>
                                </div>
                            </div>
                        )}

                        {handoffs.length > 0 && (
                            <div className="lv-subsection">
                                <h3 className="lv-subsection-title">Flow Map</h3>
                                <div className="lv-handoff-map">
                                    {handoffs.map((h, i) => {
                                        const variant = frictionVariant(h.friction_level);
                                        return (
                                            <div
                                                key={i}
                                                className={`lv-flow-row lv-flow-${variant}`}
                                            >
                                                <div className="lv-flow-node">
                                                    <span className="lv-flow-node-label">From</span>
                                                    <strong>{toLabel(h.from_role) || 'Unspecified'}</strong>
                                                </div>
                                                <div className="lv-flow-connector">
                                                    <span className="lv-flow-trigger">{toLabel(h.trigger) || 'handoff'}</span>
                                                    <span className="lv-flow-line">
                                                        <span className="lv-flow-dot" />
                                                    </span>
                                                    {h.current_method && (
                                                        <span className="lv-flow-method">{toLabel(h.current_method)}</span>
                                                    )}
                                                </div>
                                                <div className="lv-flow-node">
                                                    <span className="lv-flow-node-label">To</span>
                                                    <strong>{toLabel(h.to_role) || 'Unspecified'}</strong>
                                                </div>
                                                <div className="lv-flow-signals">
                                                    {h.friction_level && (
                                                        <Badge variant={variant}>{prettyToken(h.friction_level)}</Badge>
                                                    )}
                                                    {h.frequency && (
                                                        <Badge variant="default">{prettyToken(h.frequency)}</Badge>
                                                    )}
                                                    {h.cross_department && <Badge variant="info">cross-dept</Badge>}
                                                </div>
                                                {h.notes && (
                                                    <div className="lv-flow-note">{toLabel(h.notes)}</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {crossDeptTouchpoints.length > 0 && (
                            <div className="lv-subsection">
                                <h3 className="lv-subsection-title">Cross-Department Touchpoints</h3>
                                <div className="lv-touchpoint-grid">
                                    {crossDeptTouchpoints.map((t, i) => (
                                        <div key={i} className={`lv-touchpoint-card lv-touchpoint-${healthVariant(t.health)}`}>
                                            <div className="lv-touchpoint-header">
                                                <strong>{toLabel(t.with_department)}</strong>
                                                {t.health && <Badge variant={healthVariant(t.health)}>{prettyToken(t.health)}</Badge>}
                                            </div>
                                            {t.nature_of_interaction && (
                                                <p>{toLabel(t.nature_of_interaction)}</p>
                                            )}
                                            {t.specific_friction && (
                                                <div className="lv-touchpoint-friction">
                                                    <AlertCircle size={13} />
                                                    {toLabel(t.specific_friction)}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </SectionCard>
                )}

                {/* ---- Technology Health ---- */}
                <SectionCard icon={Monitor} iconColor="#5856d6" title="Technology Health" tag="Validation Only">
                    <div className="lv-tech-snapshot">
                        {tech.overall_satisfaction && (
                            <div className={`lv-health-dial lv-health-${healthVariant(tech.overall_satisfaction)}`}>
                                <Gauge size={18} />
                                <span>Overall Satisfaction</span>
                                <strong>{prettyToken(tech.overall_satisfaction)}</strong>
                            </div>
                        )}
                        <div className="lv-health-dial lv-health-info">
                            <Monitor size={18} />
                            <span>Tool Signals</span>
                            <strong>{techSignalCount}</strong>
                        </div>
                    </div>

                    {tech.technology_overview && (
                        <p className="lv-summary-text">{toLabel(tech.technology_overview)}</p>
                    )}

                    <div className="lv-tech-grid">
                        {techGroups.map(group => {
                            const Icon = group.icon;
                            if (group.items.length === 0) return null;
                            return (
                                <div key={group.title} className={`lv-tech-card lv-tech-${group.variant}`}>
                                    <div className="lv-tech-card-header">
                                        <Icon size={16} />
                                        <span>{group.title}</span>
                                        <strong>{group.items.length}</strong>
                                    </div>
                                    <div className="lv-tech-items">
                                        {group.items.map((item, i) => (
                                            <span key={i} className="lv-tech-item">{toLabel(item)}</span>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {(tech.integration_challenges || tech.training_needs) && (
                        <div className="lv-insight-strip">
                            {tech.integration_challenges && (
                                <div className="lv-insight-item">
                                    <span>Integration</span>
                                    <p>{toLabel(tech.integration_challenges)}</p>
                                </div>
                            )}
                            {tech.training_needs && (
                                <div className="lv-insight-item">
                                    <span>Training / Adoption</span>
                                    <p>{toLabel(tech.training_needs)}</p>
                                </div>
                            )}
                        </div>
                    )}
                </SectionCard>

                {/* ---- Collaboration & Communication ---- */}
                <SectionCard icon={MessageCircle} iconColor="#43a047" title="Collaboration & Communication" tag="Validation Only">
                    {collab.overall_health && (
                        <div className={`lv-health-dial lv-health-${healthVariant(collab.overall_health)}`}>
                            <MessageCircle size={18} />
                            <span>Overall Health</span>
                            <strong>{prettyToken(collab.overall_health)}</strong>
                        </div>
                    )}

                    {collabDimensions.length > 0 && (
                        <div className="lv-collab-grid">
                            {collabDimensions.map(item => (
                                <div key={item.label} className={`lv-collab-card lv-collab-${item.variant}`}>
                                    <div className="lv-collab-card-header">
                                        <span>{item.label}</span>
                                    </div>
                                    <p>{toLabel(item.value)}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {(asList(collab.strengths).length > 0 || asList(collab.improvement_areas).length > 0) && (
                        <div className="lv-collab-signals">
                            {asList(collab.strengths).length > 0 && (
                                <div className="lv-collab-signal-block lv-collab-signal-positive">
                                    <h3 className="lv-subsection-title lv-positive">Strengths</h3>
                                    <div className="lv-badge-list">
                                        {asList(collab.strengths).map((item, i) => (
                                            <Badge key={i} variant="positive">{toLabel(item)}</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {asList(collab.improvement_areas).length > 0 && (
                                <div className="lv-collab-signal-block lv-collab-signal-warning">
                                    <h3 className="lv-subsection-title lv-warning">Improvement Areas</h3>
                                    <div className="lv-badge-list">
                                        {asList(collab.improvement_areas).map((item, i) => (
                                            <Badge key={i} variant="warning">{toLabel(item)}</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </SectionCard>

                <div className="lv-validation-banner lv-validation-banner-bottom">
                    <Shield size={16} />
                    <div>
                        <strong>Validation checkpoint</strong>
                        <span>
                            Next step is discussion and correction of the themes above. Action planning should wait until stakeholders validate the data and framing.
                        </span>
                    </div>
                </div>

                {/* ---- Footer ---- */}
                <footer className="lv-footer">
                    <Shield size={14} />
                    <p>
                        This report contains anonymized, aggregated feedback.
                        Individual responses are not identified.
                    </p>
                    <p className="lv-footer-brand">AI Work Audit &bull; Org Scaling &amp; Systems Readiness</p>
                </footer>
            </div>
        </div>
    );
}

export default LeadershipViewPage;
